/**
 * toast — a transient on-screen notification (2026-09-23).
 *
 * The status bus (`atlas:status`) writes the debug panel's status row and plays a sound; nothing
 * ever put a REASON in front of the player where they were looking. A toast is a card at the
 * bottom-centre of the screen that names what happened — "Cannot place it there — in the water",
 * "Not enough lots" — and leaves on its own after `ms` (5 s by default). Newest at the bottom,
 * at most `MAX_TOASTS` on screen, never blocking a click (pointer-events none). Raised through
 * `bus.emit('atlas:toast', …)` (AtlasHudModule listens) or `showToast` directly.
 */

export type ToastKind = 'error' | 'info' | 'success';

export const TOAST_MS = 5000;
const MAX_TOASTS = 4;
const FADE_MS = 350;

const COLORS: Record<ToastKind, { border: string; text: string; glyph: string }> = {
  error: { border: 'color-mix(in srgb, var(--ax-bad) 75%, transparent)', text: 'color-mix(in srgb, var(--ax-bad) 55%, var(--ax-text))', glyph: '✕' },
  info: { border: 'color-mix(in srgb, var(--ax-accent) 60%, transparent)', text: 'var(--ax-text)', glyph: '·' },
  success: { border: 'color-mix(in srgb, var(--ax-good) 70%, transparent)', text: 'color-mix(in srgb, var(--ax-good) 55%, var(--ax-text))', glyph: '✓' },
};

function container(): HTMLElement {
  let el = document.getElementById('atlas-toasts');
  if (!el) {
    el = document.createElement('div');
    el.id = 'atlas-toasts';
    Object.assign(el.style, {
      position: 'fixed', left: '50%', bottom: '150px', transform: 'translateX(-50%)', zIndex: '80',
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', pointerEvents: 'none',
      maxWidth: 'min(640px, 92vw)',
    } as Partial<CSSStyleDeclaration>);
    document.body.appendChild(el);
  }
  return el;
}

/** Show a toast; returns its element (a probe reads `#atlas-toasts [data-toast]`). */
export function showToast(text: string, kind: ToastKind = 'info', ms = TOAST_MS): HTMLElement {
  const root = container();
  while (root.children.length >= MAX_TOASTS) root.firstElementChild?.remove();
  const c = COLORS[kind];
  const el = document.createElement('div');
  el.setAttribute('data-toast', kind);
  Object.assign(el.style, {
    padding: '10px 16px', borderRadius: 'var(--ax-radius)', background: 'color-mix(in srgb, var(--ax-bg) 92%, transparent)', color: c.text,
    border: `1px solid ${c.border}`, font: '14px/1.4 "IBM Plex Mono", ui-monospace, monospace',
    boxShadow: '0 6px 24px rgba(0, 0, 0, 0.45)', opacity: '0', transition: `opacity ${FADE_MS}ms ease, transform ${FADE_MS}ms ease`,
    transform: 'translateY(8px)', textAlign: 'center', whiteSpace: 'normal',
  } as Partial<CSSStyleDeclaration>);
  const glyph = document.createElement('span');
  glyph.textContent = `${c.glyph} `;
  glyph.style.opacity = '0.8';
  el.appendChild(glyph);
  el.appendChild(document.createTextNode(text));
  root.appendChild(el);
  requestAnimationFrame(() => { el.style.opacity = '1'; el.style.transform = 'translateY(0)'; });
  const leave = (): void => {
    el.style.opacity = '0';
    el.style.transform = 'translateY(8px)';
    setTimeout(() => el.remove(), FADE_MS + 20);
  };
  setTimeout(leave, Math.max(500, ms));
  return el;
}
